import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, FileSpreadsheet, RefreshCw, FileText, CheckCircle2, Edit, AlertTriangle,
  Banknote, Hourglass, FileCheck, Receipt, XCircle,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useInvoices, useValidateInvoice, useCancelInvoice, useDownloadInvoicePdf } from '@/hooks/useInvoices';
import { useIndicateursFinanciers } from '@/hooks/useBetonSprint4';
import { toast } from '@/stores/toastStore';
import { InvoiceFormDialog } from '@/components/finance/InvoiceFormDialog';
import { ModaleReglement } from '@/components/finance/ModaleReglement';
import { InvoiceDrawer } from '@/components/finance/InvoiceDrawer';
import { ContactFormDialog } from '@/components/crm/ContactFormDialog';
import { useContact } from '@/hooks/useCrm';
import {
  INVOICE_STATUS_LABELS,
  type Invoice,
  type InvoiceStatus,
} from '@/types';
import { formatFCFA, formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { exportToXLSX, exportToPDF, buildExportFilename, type ExportColumn } from '@/utils/exports';
import { demander } from '@/components/ui/Fenetres';

const fmtDateShort = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};

const STATUS_BADGE: Record<InvoiceStatus, string> = {
  proforma:  'ds-bd ds-bd-p',
  draft:     'ds-bd ds-bd-n',
  sent:      'ds-bd ds-bd-i',
  partial:   'ds-bd ds-bd-w',
  paid:      'ds-bd ds-bd-s',
  overdue:   'ds-bd ds-bd-d',
  cancelled: 'ds-bd ds-bd-n',
};

type Tab = 'all' | 'client' | 'supplier' | 'overdue' | 'proforma';

const isOverdue = (inv: Invoice): boolean => {
  if (inv.status === 'paid' || inv.status === 'cancelled' || inv.status === 'proforma') return false;
  return new Date(inv.dueDate) < new Date();
};

/**
 * Factures pixel-perfect CTO-FRONT-002.
 * Référence : design-factures-v1.html
 */
export const InvoicesPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [tab, setTab]               = useState<Tab>('all');
  const [search, setSearch]         = useState('');
  const [status, setStatus]         = useState<InvoiceStatus | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<Invoice | undefined>();
  const [drawerId, setDrawerId]     = useState<string | null>(null);
  // TICKET-004 C-04 : lien direct depuis la page Validations (/finance/invoices?ouvrir=<id>).
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get('ouvrir');
    if (id) { setDrawerId(id); params.delete('ouvrir'); setParams(params, { replace: true }); }
  }, [params, setParams]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInfo, setActionInfo]   = useState<string | null>(null);
  // Facture ouverte dans la modale d'encaissement (null = modale fermée).
  const [aEncaisser, setAEncaisser]   = useState<Invoice | null>(null);
  // TICKET-004 (C-01) : client dont les mentions OHADA manquent au PDF — sa fiche s'ouvre ici, pré-remplie.
  const [ficheClient, setFicheClient] = useState<string | null>(null);
  const [ficheOuverte, setFicheOuverte] = useState(false);
  const contactACompleter = useContact(ficheClient ?? undefined);

  const { data, isLoading, refetch } = useInvoices({ limit: 200 });
  const validate = useValidateInvoice();
  const cancel   = useCancelInvoice();
  const downloadPdf = useDownloadInvoicePdf();

  // ── State pour le dialog d'annulation (MANQUE-01) ─────────
  const [cancelTarget, setCancelTarget] = useState<Invoice | null>(null);
  const [cancelMotif,  setCancelMotif]  = useState('');

  const allInvoices = data?.data ?? [];

  // ── KPI strip — LOT A ──────────────────────────────────────────────────
  // Cet écran n'additionne plus AUCUN montant de facture. Il affichait
  // 19 320 724 FCFA d'encours le 22/09/2026 (somme des TTC, règlements
  // ignorés, brouillons inclus) quand la balance âgée en affichait
  // 9 320 724 et le tableau de bord DG 5 186 600. Les montants viennent
  // désormais de /finance/indicateurs, seule source autorisée.
  const { data: indicateurs } = useIndicateursFinanciers();

  const montants = useMemo(() => {
    const zero = { encours: 0, brouillons: 0, retard: 0, retardCount: 0, encaisse: 0, nbCreances: 0 };
    if (!indicateurs) return zero;
    const b = indicateurs.balanceAgee;
    return {
      encours:     Number(indicateurs.encoursClientsCents),
      brouillons:  Number(indicateurs.encoursBrouillonsCents),
      retard:      Number(b.enRetard.encoursCents),
      retardCount: b.enRetard.nbFactures,
      encaisse:    Number(indicateurs.encaissementsMoisCents),
      nbCreances:  b.nbFactures,
    };
  }, [indicateurs]);

  // Compteurs de PIÈCES (pas de montants) : ils restent locaux, ils ne
  // peuvent pas diverger d'un écran à l'autre.
  const kpis = useMemo(() => ({
    proformaCount: allInvoices.filter((i) => i.status === 'proforma').length,
    brouillonCount: allInvoices.filter((i) => i.status === 'draft' && !i.journalEntryId).length,
  }), [allInvoices]);

  // Filtres + tabs
  const filtered = useMemo(() => {
    let list = allInvoices;
    if (tab === 'client')   list = list.filter((i) => i.invoiceType === 'client');
    if (tab === 'supplier') list = list.filter((i) => i.invoiceType === 'supplier');
    if (tab === 'overdue')  list = list.filter(isOverdue);
    if (tab === 'proforma') list = list.filter((i) => i.status === 'proforma');
    if (status) list = list.filter((i) => i.status === status);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((i) =>
        i.reference.toLowerCase().includes(q) ||
        i.contact?.companyName?.toLowerCase().includes(q) ||
        i.contact?.fullName?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allInvoices, tab, status, search]);

  /**
   * Ouvre la saisie d'encaissement. Remplace l'ancien confirm() tout-ou-rien,
   * qui ne savait que solder la facture entière : un acompte était alors
   * impossible à enregistrer sans mentir sur le montant reçu.
   */
  const handlePay = (id: string) => {
    setActionError(null);
    setActionInfo(null);
    const inv = allInvoices.find((i) => i.id === id);
    if (inv) setAEncaisser(inv);
  };

  const handleValidate = async (id: string) => {
    setActionError(null);
    setActionInfo(null);
    // TICKET-004 C-03 : fenêtre de l'application (la boîte native, bloquée ou invisible, rendait « Valider » muet).
    if (!(await demander.confirmer({
      titre: 'Convertir cette proforma en facture finale ?',
      message: '→ Nouvelle référence FACT-… générée\n→ Écriture comptable de vente passée automatiquement\n'
        + '→ Production lancée automatiquement (commande + ordres de fabrication) pour le béton ou l’impression',
      confirmer: 'Valider en facture',
    }))) return;
    try {
      const result: any = await validate.mutateAsync(id);
      // Feedback PDG : afficher le résultat du bridge production si présent
      const prod = result?.production;
      if (prod && !prod.skipped && prod.commandeRef) {
        setActionInfo(
          `✅ Facture validée. Production déclenchée : commande ${prod.commandeRef} + ${prod.ordresCount} OF créés` +
          (prod.lignesIgnorees > 0 ? ` (${prod.lignesIgnorees} ligne(s) libre(s) sans OF)` : '')
        );
      } else if (prod?.skipped && prod.reason === 'METIER_NO_PRODUCTION') {
        setActionInfo(`✅ Facture validée. Pas de production déclenchée (métier sans OF).`);
      } else if (prod?.skipped && prod.reason === 'NO_CATALOG_LINES') {
        setActionInfo(`✅ Facture validée. Aucun OF créé (toutes les lignes sont en saisie libre, hors catalogue).`);
      } else {
        setActionInfo(`✅ Facture validée.`);
      }
    }
    catch (err) { setActionError(extractError(err).message); }
  };

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit = (inv: Invoice) => { setEditing(inv); setDialogOpen(true); };

  /** Ticket SIMGROUP-2026-003 Sprint 2 — Télécharge le PDF OHADA */
  const handleDownloadPdf = async (id: string) => {
    try {
      const { filename } = await downloadPdf.mutateAsync(id);
      toast.success('PDF téléchargé', filename);
    } catch (err: any) {
      const code = err?.code ?? 'PDF_ERROR';
      const msg  = err?.message ?? 'Impossible de générer le PDF';
      // Messages métier explicites : MISSING_LEGAL_EMITTER / MISSING_LEGAL_CLIENT
      if (code === 'MISSING_LEGAL_EMITTER') {
        toast.error('Mentions légales émetteur manquantes', msg + ' Éditer via Holding → Fiches sociétés → Mentions légales.');
      } else if (code === 'MISSING_LEGAL_CLIENT') {
        // TICKET-004 (C-01) : l'action prescrite existe désormais — la fiche s'ouvre directement ici.
        const inv = allInvoices.find((x) => x.id === id);
        setFicheClient(inv?.contactId ?? null);
        toast.error('Mentions légales client manquantes', msg);
      } else {
        toast.error('Échec de génération PDF', msg);
      }
    }
  };

  const openCancel = (inv: Invoice) => { setCancelTarget(inv); setCancelMotif(''); };
  const confirmCancel = async () => {
    if (!cancelTarget) return;
    if (cancelMotif.trim().length < 5) {
      setActionError('Le motif d\'annulation doit contenir au moins 5 caractères.');
      return;
    }
    setActionError(null); setActionInfo(null);
    try {
      await cancel.mutateAsync({ id: cancelTarget.id, motif: cancelMotif.trim() });
      setActionInfo(`✅ Pièce ${cancelTarget.reference} annulée. Elle reste consultable via le filtre "Annulée".`);
      setCancelTarget(null);
      setCancelMotif('');
      setDrawerId(null);
    } catch (err) {
      setActionError(extractError(err).message);
    }
  };

  // ── Export Excel / PDF ─────────────────────────────────────
  const exportColumns: ExportColumn<Invoice>[] = [
    { header: 'Référence',  accessor: (i) => i.reference },
    { header: 'Type',       accessor: (i) => i.invoiceType === 'client' ? 'Client' : 'Fournisseur' },
    { header: 'Date',       accessor: (i) => fmtDateShort(i.issueDate) },
    { header: 'Échéance',   accessor: (i) => fmtDateShort(i.dueDate) },
    { header: 'Contact',    accessor: (i) => i.contact?.companyName ?? i.contact?.fullName ?? '—' },
    { header: 'Statut',     accessor: (i) => INVOICE_STATUS_LABELS[i.status] },
    { header: 'Total HT',   accessor: (i) => Number(BigInt(i.subtotalCents)) / 100, align: 'right' },
    { header: 'TVA',        accessor: (i) => Number(BigInt(i.taxCents))      / 100, align: 'right' },
    { header: 'Total TTC',  accessor: (i) => Number(BigInt(i.totalCents))    / 100, align: 'right' },
    { header: 'Payé',       accessor: (i) => Number(BigInt(i.paidAmountCents)) / 100, align: 'right' },
    { header: 'Reste',      accessor: (i) => (Number(BigInt(i.totalCents)) - Number(BigInt(i.paidAmountCents))) / 100, align: 'right' },
  ];

  const onExportXLSX = () => exportToXLSX({
    filename:  buildExportFilename('Factures', filiale?.filialeKey, 'xlsx'),
    sheetName: 'Factures',
    columns:   exportColumns,
    rows:      filtered,
  });

  const onExportPDF = () => exportToPDF({
    title:    `Factures — ${filiale?.fullName ?? ''}`,
    subtitle: `${filtered.length} facture${filtered.length > 1 ? 's' : ''} · ${tab === 'all' ? 'toutes' : tab === 'client' ? 'clients' : tab === 'supplier' ? 'fournisseurs' : tab === 'overdue' ? 'en retard' : 'proforma'}`,
    filename: buildExportFilename('Factures', filiale?.filialeKey, 'pdf'),
    orientation: 'landscape',
    columns:  exportColumns,
    rows:     filtered,
  });

  return (
    <>
      <Topbar
        title="Facturation"
        subtitle={filiale ? `Clients & Fournisseurs · ${filiale.fullName}` : 'Facturation'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} aria-label="Rafraîchir">
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void onExportXLSX()} disabled={filtered.length === 0} title="Exporter Excel">
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void onExportPDF()} disabled={filtered.length === 0} title="Exporter PDF">
              <FileText size={14} /> PDF
            </button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}>
              <Plus size={14} /> Nouvelle facture
            </button>
          </>
        }
      />

      <div className="ds-content">
        {ficheClient && (
          <div className="ds-alert-strip ds-alert-w" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={20} /></span>
            <div className="ds-alert-txt">
              Le PDF de la facture est refusé : le RCCM ou le NCC du client
              {contactACompleter.data ? ` ${contactACompleter.data.companyName ?? contactACompleter.data.fullName}` : ''} manque.
            </div>
            <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginLeft: 'auto' }} disabled={!contactACompleter.data} onClick={() => setFicheOuverte(true)}>
              Compléter la fiche client
            </button>
            <button onClick={() => setFicheClient(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }} aria-label="Fermer">×</button>
          </div>
        )}
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={20} /></span>
            <div className="ds-alert-txt">{actionError}</div>
            <button onClick={() => setActionError(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }} aria-label="Fermer">×</button>
          </div>
        )}
        {actionInfo && (
          <div className="ds-alert-strip ds-alert-s" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><CheckCircle2 size={20} /></span>
            <div className="ds-alert-txt">{actionInfo}</div>
            <button onClick={() => setActionInfo(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }} aria-label="Fermer">×</button>
          </div>
        )}

        {/* KPI Strip */}
        <div className="ds-kpi-strip">
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Encours clients</div>
            <div className="ds-ks-value">{formatNumber(montants.encours / 100)} <span style={{ fontSize: 12, color: 'var(--tx-3)' }}>FCFA</span></div>
            <div className="ds-ks-sub">{indicateurs ? `${montants.nbCreances} créance${montants.nbCreances > 1 ? 's' : ''} émise${montants.nbCreances > 1 ? 's' : ''}` : '—'}</div>
          </div>
          <div className="ds-ks ks-red">
            <div className="ds-ks-label">En retard ⚠️</div>
            <div className="ds-ks-value" style={{ color: 'var(--danger)' }}>{formatNumber(montants.retard / 100)} <span style={{ fontSize: 12 }}>FCFA</span></div>
            <div className="ds-ks-sub">{montants.retardCount} facture{montants.retardCount > 1 ? 's' : ''}</div>
          </div>
          {/* Assertion n° 2 : les brouillons sont restitués à part, jamais
              agrégés à l'encours — même libellé sur les trois écrans. */}
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Brouillons (hors encours)</div>
            <div className="ds-ks-value">{formatNumber(montants.brouillons / 100)} <span style={{ fontSize: 12 }}>FCFA</span></div>
            <div className="ds-ks-sub">{kpis.brouillonCount} facture{kpis.brouillonCount > 1 ? 's' : ''} non comptabilisée{kpis.brouillonCount > 1 ? 's' : ''}</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Encaissé ce mois</div>
            <div className="ds-ks-value" style={{ color: 'var(--success)' }}>{formatNumber(montants.encaisse / 100)} <span style={{ fontSize: 12 }}>FCFA</span></div>
            <div className="ds-ks-sub">{kpis.proformaCount} proforma{kpis.proformaCount > 1 ? 's' : ''} en attente</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="ds-tabs-bar">
          <button className={`ds-tab-btn${tab === 'all' ? ' on' : ''}`}     onClick={() => setTab('all')}>📑 Toutes ({allInvoices.length})</button>
          <button className={`ds-tab-btn${tab === 'client' ? ' on' : ''}`}  onClick={() => setTab('client')}>🧾 Clients ({allInvoices.filter((i) => i.invoiceType === 'client').length})</button>
          <button className={`ds-tab-btn${tab === 'supplier' ? ' on' : ''}`} onClick={() => setTab('supplier')}>🏭 Fournisseurs ({allInvoices.filter((i) => i.invoiceType === 'supplier').length})</button>
          <button className={`ds-tab-btn${tab === 'overdue' ? ' on' : ''}`}  onClick={() => setTab('overdue')}>⚠️ En retard ({allInvoices.filter(isOverdue).length})</button>
          <button className={`ds-tab-btn${tab === 'proforma' ? ' on' : ''}`} onClick={() => setTab('proforma')}>📝 Proformas ({kpis.proformaCount})</button>
        </div>

        {/* Filtres */}
        <div className="ds-filters">
          <div className="ds-sw">
            <input
              className="ds-fi ds-fi-search"
              type="search"
              placeholder="Client, référence, montant..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="ds-fi"
            style={{ width: 'auto' }}
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatus | '')}
          >
            <option value="">Tous statuts</option>
            {(Object.keys(INVOICE_STATUS_LABELS) as InvoiceStatus[]).map((s) => (
              <option key={s} value={s}>{INVOICE_STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>

        {/* Grid factures */}
        {isLoading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="ds-panel" style={{ padding: 48, textAlign: 'center' }}>
            <Receipt size={36} style={{ margin: '0 auto 12px', color: 'var(--tx-3)' }} />
            <p style={{ fontSize: 14, color: 'var(--tx-2)' }}>Aucune facture</p>
            <button className="ds-btn ds-btn-p" style={{ marginTop: 12 }} onClick={openCreate}>
              <Plus size={14} /> Créer la première facture
            </button>
          </div>
        ) : (
          <div className="ds-inv-grid">
            {filtered.map((inv) => (
              <InvoiceCard
                key={inv.id}
                invoice={inv}
                onClick={() => setDrawerId(inv.id)}
                onEdit={() => openEdit(inv)}
                onValidate={() => handleValidate(inv.id)}
                onPay={() => handlePay(inv.id)}
                onCancel={() => openCancel(inv)}
                onPrint={() => void handleDownloadPdf(inv.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal création / édition */}
      <InvoiceFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} invoice={editing} />

      {/* Saisie d'un encaissement, total ou partiel */}
      <ContactFormDialog
        open={ficheOuverte && Boolean(contactACompleter.data)}
        contact={contactACompleter.data ?? null}
        onClose={() => setFicheOuverte(false)}
        onSaved={(c) => {
          if (c.rccmClient && c.nccClient) setFicheClient(null);
          toast.success('Fiche client enregistrée', 'Vous pouvez relancer le PDF de la facture.');
        }}
      />
      <ModaleReglement
        open={Boolean(aEncaisser)}
        invoice={aEncaisser}
        onClose={() => setAEncaisser(null)}
        onSuccess={(resultat) => {
          setActionInfo(
            resultat.solde
              ? `Encaissement enregistré — ${resultat.invoice.reference} est soldée.`
              : `Encaissement enregistré — il reste ${formatMontant(resultat.restantCents)} FCFA à recouvrer sur ${resultat.invoice.reference}.`,
          );
          setDrawerId(null);
          void refetch();
        }}
      />

      {/* Drawer détail */}
      <InvoiceDrawer
        onFicheClient={(cid) => setFicheClient(cid)}
        invoiceId={drawerId}
        onClose={() => setDrawerId(null)}
        onPay={(id) => handlePay(id)}
        onValidate={(id) => handleValidate(id)}
        onCancel={(id) => {
          const inv = allInvoices.find((i) => i.id === id);
          if (inv) openCancel(inv);
        }}
      />

      {/* Dialog d'annulation (MANQUE-01) — motif obligatoire */}
      {cancelTarget && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) { setCancelTarget(null); setCancelMotif(''); } }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--bg-1, #fff)', borderRadius: 8, padding: 24,
              width: 'min(500px, 92vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 16, color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <XCircle size={20} /> Annuler la pièce {cancelTarget.reference} ?
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--tx-2)' }}>
              Client : <strong>{cancelTarget.contact?.companyName ?? cancelTarget.contact?.fullName ?? '—'}</strong><br />
              Montant : <strong>{formatMontant(cancelTarget.totalCents)} FCFA TTC</strong>
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--tx-3)' }}>
              L'annulation est <strong>définitive</strong>. La pièce restera consultable via le filtre « Annulée » mais sortira des KPI d'encours et du pipeline. Un motif est requis pour l'audit.
            </p>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: 'var(--tx-2)' }}>
              Motif d'annulation <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <textarea
              className="ds-fi"
              rows={3}
              placeholder="Ex : Doublon de FACT-2026-002 · Erreur de saisie · Demande client annulée…"
              value={cancelMotif}
              onChange={(e) => setCancelMotif(e.target.value)}
              style={{ width: '100%', resize: 'vertical', minHeight: 80, marginBottom: 4 }}
              autoFocus
              maxLength={500}
            />
            <div style={{ fontSize: 11, color: cancelMotif.trim().length < 5 ? 'var(--danger)' : 'var(--tx-3)', marginBottom: 16 }}>
              {cancelMotif.trim().length < 5
                ? `Encore ${5 - cancelMotif.trim().length} caractère(s) minimum`
                : `${cancelMotif.length} / 500 caractères`}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="ds-btn ds-btn-g"
                onClick={() => { setCancelTarget(null); setCancelMotif(''); }}
                disabled={cancel.isPending}
              >
                Retour
              </button>
              <button
                type="button"
                onClick={() => void confirmCancel()}
                disabled={cancelMotif.trim().length < 5 || cancel.isPending}
                style={{
                  background: 'var(--danger)', color: '#fff', border: 'none',
                  padding: '8px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13,
                  cursor: cancelMotif.trim().length < 5 || cancel.isPending ? 'not-allowed' : 'pointer',
                  opacity: cancelMotif.trim().length < 5 || cancel.isPending ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <XCircle size={14} /> {cancel.isPending ? 'Annulation…' : 'Confirmer l\'annulation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── InvoiceCard ───────────────────────────────────────────────

const InvoiceCard = ({
  invoice, onClick, onEdit, onValidate, onPay, onCancel, onPrint,
}: {
  invoice:  Invoice;
  onClick:  () => void;
  onEdit:   () => void;
  onValidate: () => void;
  onPay:    () => void;
  onCancel: () => void;
  onPrint:  () => void;
}) => {
  const total       = Number(BigInt(invoice.totalCents));
  const paid        = Number(BigInt(invoice.paidAmountCents));
  const subtotal    = Number(BigInt(invoice.subtotalCents));
  const tax         = Number(BigInt(invoice.taxCents));
  const tvaRate     = subtotal > 0 ? Math.round((tax / (subtotal - subtotal * parseFloat(invoice.remisePct ?? '0') / 100)) * 100) : 0;
  const progressPct = total > 0 ? Math.round((paid / total) * 100) : 0;
  const overdue     = isOverdue(invoice);
  const isProforma  = invoice.status === 'proforma';
  const isDraft     = invoice.status === 'draft';
  const isPaid      = invoice.status === 'paid';
  // MANQUE-01 : annulation ouverte sur proforma + brouillon SANS écriture comptable
  const isCancellable = (isProforma || isDraft) && !invoice.journalEntryId;

  const dueLabel = isPaid && invoice.paidAt
    ? <>Payée le <span style={{ color: 'var(--success)' }}>{fmtDateShort(invoice.paidAt)}</span></>
    : overdue
      ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{fmtDateShort(invoice.dueDate)} ✗</span>
      : <span>{fmtDateShort(invoice.dueDate)}</span>;

  return (
    <div
      className={`ds-inv-card${isProforma ? ' dashed' : ''}${isPaid ? ' paid' : ''}`}
      onClick={onClick}
    >
      <div className="ds-inv-card-header">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="ds-inv-client">{invoice.contact?.companyName ?? invoice.contact?.fullName ?? '—'}</div>
          <div className="ds-inv-ref">{invoice.reference} · {fmtDateShort(invoice.issueDate)}{invoice.version > 1 && ` · v${invoice.version}`}</div>
        </div>
        <span className={STATUS_BADGE[invoice.status]}>
          {overdue ? '⚠️ En retard' : INVOICE_STATUS_LABELS[invoice.status]}
        </span>
      </div>

      <div className="ds-inv-card-body">
        <div className="ds-inv-amount" style={isPaid ? { color: 'var(--success)' } : undefined}>
          {formatNumber(total / 100)} <span className="ds-inv-amount-suffix">FCFA TTC</span>
        </div>
        <div className="ds-inv-meta">
          <div className="ds-inv-meta-item"><label>Échéance</label><span>{dueLabel}</span></div>
          <div className="ds-inv-meta-item"><label>TVA ({tvaRate}%)</label><span>{formatNumber(tax / 100)} FCFA</span></div>
          <div className="ds-inv-meta-item"><label>Payé</label>
            <span style={paid > 0 ? { color: 'var(--success)' } : { color: 'var(--tx-3)' }}>
              {paid > 0 ? `${formatNumber(paid / 100)} FCFA` : '—'}
            </span>
          </div>
          {isProforma ? (
            <div className="ds-inv-meta-item"><label>Version</label><span>v{invoice.version}</span></div>
          ) : (
            <div className="ds-inv-meta-item"><label>Reste dû</label>
              <span style={total - paid > 0 ? { color: 'var(--warning)', fontWeight: 600 } : undefined}>
                {formatNumber((total - paid) / 100)} FCFA
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="ds-inv-card-footer" onClick={(e) => e.stopPropagation()}>
        <div className="ds-inv-progress-wrap">
          <div className="ds-inv-progress-label">
            <span>Règlement</span>
            <span style={isPaid ? { color: 'var(--success)' } : overdue ? { color: 'var(--danger)' } : undefined}>{progressPct}%</span>
          </div>
          <div className="ds-inv-progress-bar">
            <div
              className="ds-inv-progress-fill"
              style={{
                width: `${progressPct}%`,
                background:
                  isPaid    ? 'linear-gradient(90deg,var(--success),#6ee7b7)' :
                  overdue   ? 'var(--danger)' :
                  paid > 0  ? 'linear-gradient(90deg,var(--warning),#fcd34d)' :
                              undefined,
              }}
            />
          </div>
        </div>
        <div className="ds-inv-actions">
          {isProforma && (
            <button
              className="ds-inv-action-btn"
              title="Valider la proforma → facture finale + lance la production"
              onClick={onValidate}
              style={{
                background: 'var(--success, #10b981)',
                color: '#fff',
                fontWeight: 600,
                padding: '6px 12px',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              <FileCheck size={14} /> Valider
            </button>
          )}
          <button
            className="ds-inv-action-btn"
            title="Imprimer / PDF"
            onClick={onPrint}
            style={{
              background: '#fff',
              border: '1px solid var(--border, #e5e7eb)',
              color: 'var(--tx-1, #111)',
              padding: '6px 10px',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            <FileText size={13} /> PDF
          </button>
          {isProforma && (
            <button
              className="ds-inv-action-btn"
              title="Modifier la proforma"
              onClick={onEdit}
              style={{
                background: '#fff',
                border: '1px solid var(--border, #e5e7eb)',
                color: 'var(--tx-2, #374151)',
                padding: '6px 10px',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              <Edit size={13} /> Modifier
            </button>
          )}
          {!isProforma && !isPaid && invoice.status !== 'cancelled' && (
            <button
              className="ds-inv-action-btn"
              title="Enregistrer un encaissement, total ou partiel"
              onClick={onPay}
              style={{
                background: 'var(--success, #10b981)',
                color: '#fff',
                fontWeight: 600,
                padding: '6px 12px',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              <Banknote size={13} /> Encaisser
            </button>
          )}
          {isCancellable && (
            <button
              className="ds-inv-action-btn"
              title="Annuler la pièce (motif obligatoire)"
              onClick={onCancel}
              style={{
                marginLeft: 'auto',
                background: '#fff',
                border: '1px solid var(--danger, #ef4444)',
                color: 'var(--danger, #ef4444)',
                padding: '6px 10px',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              <XCircle size={13} /> Annuler
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Suppress unused warnings
void Banknote; void Hourglass; void formatFCFA;
